import type { MapCountryData } from "../AnalyticsMap";

export type VendorEntry = { rank: number; name: string; count: number };
export type PriceVendorEntry = { rank: number; name: string; medianPrice: number };

export type VendorGroups<T> = {
  marketplace: T[];
  franchise: T[];
  independent: T[];
};

type ByBrandAndCategory<T> = Record<string, Record<string, T>>;

export type TopVendorsData = {
  brands: string[];
  byBrand: ByBrandAndCategory<VendorGroups<VendorEntry>>;
};

export type HighestQuantityEntry = {
  rank: number;
  country: string;
  code: string;
  count: number;
};

export type HighestQuantityData = {
  categories: string[];
  byBrand: ByBrandAndCategory<HighestQuantityEntry[]>;
};

export type MedianPriceEntry = {
  rank: number;
  country: string;
  code: string;
  medianPrice: number;
};

export type MedianPriceData = {
  byBrand: ByBrandAndCategory<MedianPriceEntry[]>;
};

export type LowestMedianData = {
  byBrand: ByBrandAndCategory<VendorGroups<PriceVendorEntry>>;
};

export type ReportMapData = {
  categories: string[];
  byBrand: ByBrandAndCategory<MapCountryData>;
};

export type MovementEntry = { rank: number; label: string; value: number };
export type MovementView = "price" | "volume";
export type MovementSegment = "clothing" | "bags";

export type FlatMovementData = {
  price: MovementEntry[];
  volume: MovementEntry[];
};

export type SegmentedMovementData = {
  segments: Record<
    MovementSegment,
    Record<MovementView, { growers: MovementEntry[]; shrinks: MovementEntry[]; baseline: number }>
  >;
};

export type BiggestMovementsData = FlatMovementData | SegmentedMovementData;

export type ArbitrageEntry = {
  rank: number;
  buyMarket: string;
  buyPrice: number;
  sellMarket: string;
  sellPrice: number;
  spread: number;
  spreadPct: number;
};

export type ArbitrageWindowsData = {
  byBrand: ByBrandAndCategory<ArbitrageEntry[]>;
};

export type BubbleVendor = {
  name: string;
  x: number;
  y: number;
  z: number;
  country?: string;
};

export type BubbleData = Record<string, Record<string, BubbleVendor[]>>;

export type VendorBubbleData = {
  independent: BubbleData;
  marketplace: BubbleData;
};

export type PriceDistribution = Record<string, Record<string, number[]>>;

export type PriceDistributionData = {
  independent: PriceDistribution;
  marketplace: PriceDistribution;
};

export type ReportPageProps = {
  topVendorsData: TopVendorsData;
  highestQuantityData: HighestQuantityData;
  medianPriceData: MedianPriceData;
  lowestMedianData: LowestMedianData;
  mapData: ReportMapData;
  biggestMovementsData: BiggestMovementsData;
  arbitrageWindowsData: ArbitrageWindowsData;
  vendorBubbleData: VendorBubbleData;
  priceDistributionData: PriceDistributionData;
};

export type ViolinBrand = { brand: string; prices: number[] };

export const formatNumber = (value: number) => value.toLocaleString();

export function getViolinData(distribution: PriceDistribution, brand: string): ViolinBrand[] {
  const vendorMap = distribution[brand] ?? {};
  return Object.entries(vendorMap)
    .map(([vendor, prices]) => ({ brand: vendor, prices }))
    .sort((a, b) => b.prices.length - a.prices.length)
    .slice(0, 10);
}

const regionNames = new Intl.DisplayNames(["en"], { type: "region" });

export function countryName(isoA2: string): string {
  return regionNames.of(isoA2) ?? isoA2;
}

export function getArbitrageData(
  data: ArbitrageWindowsData["byBrand"],
  brand: string,
  category: string
): ArbitrageEntry[] {
  const byBrand = data[brand];
  if (!byBrand) return [];
  return byBrand[category] ?? byBrand["All Categories"] ?? [];
}
